import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CheckCircle, PauseCircle, Ban, Clock, TrendingUp, Users, Search } from 'lucide-react';
import { api, compact, money } from '../../lib/api';
import { Avatar, SkeletonList, Sheet, Stars } from '../../components/ui';

const STATUSES = [['', 'All'], ['pending', '⏳ Pending'], ['approved', '✅ Approved'], ['suspended', '⚠️ Suspended'], ['blocked', '🚫 Blocked']];

export default function AdminShops() {
  const qc = useQueryClient();
  const [status, setStatus] = useState('pending');
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(null);
  const [mult, setMult] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['adminShops', status, q],
    queryFn: () => api.get('/admin/shops', { params: { status: status || undefined, q: q || undefined } }).then(r => r.data),
  });

  const statusM = useMutation({
    mutationFn: ({ id, st, reason }) => api.post(`/admin/shops/${id}/status`, { status: st, reason }),
    onSuccess: () => { toast.success('Shop updated'); qc.invalidateQueries(['adminShops']); setSel(null); },
    onError: e => toast.error(e.message),
  });
  const multM = useMutation({
    mutationFn: ({ id, multiplier }) => api.post(`/admin/shops/${id}/multiplier`, { multiplier }),
    onSuccess: () => { toast.success('Multiplier saved'); qc.invalidateQueries(['adminShops']); },
  });

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-400" />
        <input className="input pl-10" placeholder="Search shops…" value={q} onChange={e => setQ(e.target.value)} />
      </div>
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
        {STATUSES.map(([k, l]) => (
          <button key={k} onClick={() => setStatus(k)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap ${status === k ? 'bg-ink-900 text-white' : 'bg-white border border-ink-200 text-ink-500'}`}>{l}</button>))}
      </div>

      {isLoading ? <SkeletonList n={6} h="h-20" /> : (
        <div className="space-y-2">
          {data.map(s => (
            <div key={s.id} className={`card p-3 ${s.status === 'pending' ? 'ring-2 ring-amber-300' : ''}`}>
              <div className="flex items-center gap-3">
                <Avatar src={s.logo_url} name={s.name} size={44} square />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="font-bold text-sm truncate">{s.name}</p>
                    <span className={`chip ${s.status === 'approved' ? 'chip-mint' : s.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-600'}`}>
                      {s.status}</span>
                    <span className="chip-ink">{s.kind.replace('_', ' ')}</span>
                    {s.city_handle && <span className="chip-brand">{s.city_handle}</span>}
                  </div>
                  <p className="text-[11px] text-ink-400 font-semibold truncate">
                    @{s.owner_username} · {s.owner_email}
                  </p>
                  <div className="flex gap-3 text-[11px] font-bold text-ink-500 mt-0.5">
                    <span><Users size={9} className="inline" /> {compact(s.community)}</span>
                    <span><TrendingUp size={9} className="inline" /> {money(s.revenue)}</span>
                    <span>{s.orders} orders</span>
                    <span>{Number(s.points_multiplier).toFixed(2)}×</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-1.5 mt-2.5">
                {s.status !== 'approved' && (
                  <button onClick={() => statusM.mutate({ id: s.id, st: 'approved' })} className="btn-mint btn-sm flex-1">
                    <CheckCircle size={12} /> Approve</button>)}
                {s.status === 'approved' && (
                  <button onClick={() => statusM.mutate({ id: s.id, st: 'suspended', reason: prompt('Reason?') || '' })} className="btn-ghost btn-sm flex-1">
                    <PauseCircle size={12} /> Suspend</button>)}
                <button onClick={() => statusM.mutate({ id: s.id, st: 'blocked', reason: prompt('Reason?') || '' })} className="btn btn-sm bg-red-50 text-red-600 flex-1">
                  <Ban size={12} /> Block</button>
                <button onClick={() => setSel(s)} className="btn-ghost btn-sm">Boost</button>
                <Link to={`/shop/${s.slug}`} className="btn-ghost btn-sm">View</Link>
              </div>
            </div>))}
        </div>
      )}

      <Sheet open={!!sel} onClose={() => setSel(null)} title={`Boost ${sel?.name || ''}`}>
        <p className="text-sm text-ink-500 mb-3">
          Extra points multiplier applied on top of the customer's loyalty tier.
          Use for promotional campaigns.
        </p>
        <label className="label">Shop multiplier (0.1 – 10)</label>
        <div className="flex gap-2">
          <input className="input" type="number" step="0.05" min="0.1" max="10"
            defaultValue={sel?.points_multiplier} onChange={e => setMult(e.target.value)} />
          <button onClick={() => { multM.mutate({ id: sel.id, multiplier: Number(mult || sel.points_multiplier) }); setSel(null); }}
            className="btn-primary">Save</button>
        </div>
        <div className="flex gap-1.5 mt-3">
          {[1, 1.5, 2, 3].map(v => (
            <button key={v} onClick={() => { multM.mutate({ id: sel.id, multiplier: v }); setSel(null); }}
              className="btn-ghost btn-sm flex-1">{v}×</button>))}
        </div>
      </Sheet>
    </div>
  );
}